/**
 * User Preferences Page.
 *
 * Personal settings accessible from the user profile dropdown.
 * Controls theme, notification channels, notification types,
 * quiet hours, and display preferences.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sun,
  Moon,
  Monitor,
  Bell,
  BellOff,
  Mail,
  MessageSquare,
  Smartphone,
  Clock,
  Eye,
  Save,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import { useThemeStore } from "@/stores/themeStore";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";

interface Preferences {
  theme: string;
  notifications: Record<string, boolean>;
  notification_types: Record<string, boolean>;
  quiet_hours: {
    enabled: boolean;
    start: string;
    end: string;
    timezone: string;
  };
  display: {
    table_density: string;
    date_format: string;
    currency_display: string;
    number_locale: string;
  };
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  wo_assigned: "Work order assigned to me",
  wo_completed: "Work order completed",
  po_received: "Purchase order received",
  asset_sold: "Asset sold",
  deal_stage_changed: "Deal stage changed",
  automation_fired: "Automation triggered",
  message_received: "New message received",
  system_alerts: "System alerts and announcements",
};

function PreferencesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isDark, toggle: toggleTheme } = useThemeStore();

  const { data: prefs, isLoading } = useQuery<Preferences>({
    queryKey: ["user-preferences"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/auth/preferences")).data as Preferences,
  });

  const [theme, setTheme] = useState("system");
  const [channels, setChannels] = useState<Record<string, boolean>>({});
  const [types, setTypes] = useState<Record<string, boolean>>({});
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState("22:00");
  const [quietEnd, setQuietEnd] = useState("07:00");
  const [quietTimezone, setQuietTimezone] = useState("America/Chicago");
  const [tableDensity, setTableDensity] = useState("standard");
  const [dateFormat, setDateFormat] = useState("MMM d, yyyy");
  const [currencyDisplay, setCurrencyDisplay] = useState("symbol");

  // Populate form from loaded preferences.
  useEffect(() => {
    if (!prefs) return;
    setTheme(prefs.theme);
    setChannels(prefs.notifications);
    setTypes(prefs.notification_types);
    setQuietEnabled(prefs.quiet_hours.enabled);
    setQuietStart(prefs.quiet_hours.start);
    setQuietEnd(prefs.quiet_hours.end);
    setQuietTimezone(prefs.quiet_hours.timezone);
    setTableDensity(prefs.display.table_density);
    setDateFormat(prefs.display.date_format);
    setCurrencyDisplay(prefs.display.currency_display);
  }, [prefs]);

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.put("/api/v1/auth/preferences", {
          theme,
          notifications: channels,
          notification_types: types,
          quiet_hours: {
            enabled: quietEnabled,
            start: quietStart,
            end: quietEnd,
            timezone: quietTimezone,
          },
          display: {
            table_density: tableDensity,
            date_format: dateFormat,
            currency_display: currencyDisplay,
          },
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-preferences"] });
      toast.success("Preferences saved");

      // Apply theme change immediately.
      if (theme === "dark" && !isDark) toggleTheme();
      if (theme === "light" && isDark) toggleTheme();
      if (theme === "system") {
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        if (prefersDark !== isDark) toggleTheme();
      }
    },
    onError: () => toast.error("Failed to save preferences"),
  });

  const handleThemeChange = (value: string) => {
    setTheme(value);
    // Apply immediately for preview.
    if (value === "dark" && !isDark) toggleTheme();
    if (value === "light" && isDark) toggleTheme();
    if (value === "system") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      if (prefersDark !== isDark) toggleTheme();
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl py-24 text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-interactive-primary)] border-t-transparent mx-auto" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-2xl font-medium text-[var(--text-primary)]">
            Preferences
          </h2>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            Personal settings that apply to your account only.
          </p>
        </div>
      </div>

      {/* ── Appearance ── */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Eye className="h-4 w-4 text-[var(--text-tertiary)]" />
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Appearance
          </h3>
        </div>

        {/* Theme — 3-way segmented control per §16.4 */}
        <div className="mb-5">
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-2">
            Theme
          </label>
          <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--border-default)] p-0.5 bg-[var(--surface-sunken)]">
            {[
              { value: "light", label: "Light", icon: Sun },
              { value: "system", label: "System", icon: Monitor },
              { value: "dark", label: "Dark", icon: Moon },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleThemeChange(opt.value)}
                className={`flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium transition-all ${
                  theme === opt.value
                    ? "bg-[var(--surface-base)] text-[var(--text-primary)] shadow-sm border border-[var(--border-default)]"
                    : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                }`}
              >
                <opt.icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table density */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-2">
            Table density
          </label>
          <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--border-default)] p-0.5 bg-[var(--surface-sunken)]">
            {["comfortable", "standard", "compact"].map((d) => (
              <button
                key={d}
                onClick={() => setTableDensity(d)}
                className={`rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium capitalize transition-all ${
                  tableDensity === d
                    ? "bg-[var(--surface-base)] text-[var(--text-primary)] shadow-sm border border-[var(--border-default)]"
                    : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Date format */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Date format
            </label>
            <select
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
              className="block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              <option value="MMM d, yyyy">Apr 17, 2026</option>
              <option value="d MMM yyyy">17 Apr 2026</option>
              <option value="MM/dd/yyyy">04/17/2026</option>
              <option value="yyyy-MM-dd">2026-04-17</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Currency display
            </label>
            <select
              value={currencyDisplay}
              onChange={(e) => setCurrencyDisplay(e.target.value)}
              className="block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              <option value="symbol">$1,234.56</option>
              <option value="code">USD 1,234.56</option>
              <option value="name">1,234.56 US dollars</option>
            </select>
          </div>
        </div>
      </Card>

      {/* ── Notification channels ── */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-4 w-4 text-[var(--text-tertiary)]" />
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Notification delivery
          </h3>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">
          Choose how you receive notifications. At least one channel should be
          active.
        </p>

        <div className="space-y-3">
          {[
            {
              key: "in_app",
              label: "In-app notifications",
              description: "Bell icon in the header",
              icon: Bell,
            },
            {
              key: "email",
              label: "Email",
              description: "Sent to your account email",
              icon: Mail,
            },
            {
              key: "sms",
              label: "SMS",
              description: "Text messages to your phone",
              icon: Smartphone,
            },
            {
              key: "whatsapp",
              label: "WhatsApp",
              description: "Messages via WhatsApp Business",
              icon: MessageSquare,
            },
          ].map((ch) => (
            <label
              key={ch.key}
              className="flex items-center justify-between py-1 cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <ch.icon className="h-4 w-4 text-[var(--text-tertiary)] group-hover:text-[var(--text-secondary)] transition-colors" />
                <div>
                  <p className="text-sm text-[var(--text-primary)]">
                    {ch.label}
                  </p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">
                    {ch.description}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={channels[ch.key] || false}
                onClick={() =>
                  setChannels((prev) => ({ ...prev, [ch.key]: !prev[ch.key] }))
                }
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                  channels[ch.key]
                    ? "bg-[var(--color-interactive-primary)]"
                    : "bg-[var(--border-default)]"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-[240ms] ${
                    channels[ch.key] ? "translate-x-4" : "translate-x-0.5"
                  } mt-0.5`}
                  style={{
                    transitionTimingFunction:
                      "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  }}
                />
              </button>
            </label>
          ))}
        </div>
      </Card>

      {/* ── Notification types ── */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <BellOff className="h-4 w-4 text-[var(--text-tertiary)]" />
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Notification types
          </h3>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">
          Control which events trigger notifications.
        </p>

        <div className="space-y-2.5">
          {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center justify-between py-0.5 cursor-pointer"
            >
              <span className="text-sm text-[var(--text-primary)]">
                {label}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={types[key] || false}
                onClick={() =>
                  setTypes((prev) => ({ ...prev, [key]: !prev[key] }))
                }
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                  types[key]
                    ? "bg-[var(--color-interactive-primary)]"
                    : "bg-[var(--border-default)]"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-[240ms] ${
                    types[key] ? "translate-x-4" : "translate-x-0.5"
                  } mt-0.5`}
                  style={{
                    transitionTimingFunction:
                      "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  }}
                />
              </button>
            </label>
          ))}
        </div>
      </Card>

      {/* ── Quiet hours ── */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[var(--text-tertiary)]" />
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Quiet hours
            </h3>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={quietEnabled}
            onClick={() => setQuietEnabled(!quietEnabled)}
            className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
              quietEnabled
                ? "bg-[var(--color-interactive-primary)]"
                : "bg-[var(--border-default)]"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-[240ms] ${
                quietEnabled ? "translate-x-4" : "translate-x-0.5"
              } mt-0.5`}
              style={{
                transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            />
          </button>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">
          Pause non-critical notifications during off hours. System alerts and
          urgent items still come through.
        </p>

        {quietEnabled && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Start
              </label>
              <input
                type="time"
                value={quietStart}
                onChange={(e) => setQuietStart(e.target.value)}
                className="block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                End
              </label>
              <input
                type="time"
                value={quietEnd}
                onChange={(e) => setQuietEnd(e.target.value)}
                className="block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Timezone
              </label>
              <select
                value={quietTimezone}
                onChange={(e) => setQuietTimezone(e.target.value)}
                className="block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="America/New_York">Eastern</option>
                <option value="America/Chicago">Central</option>
                <option value="America/Denver">Mountain</option>
                <option value="America/Los_Angeles">Pacific</option>
                <option value="America/Anchorage">Alaska</option>
                <option value="Pacific/Honolulu">Hawaii</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* ── Save bar ── */}
      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={() => saveMutation.mutate()}
          isLoading={saveMutation.isPending}
          size="md"
        >
          <Save className="h-4 w-4" />
          Save preferences
        </Button>
      </div>
    </div>
  );
}

export default PreferencesPage;
